import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { usePrefersReducedMotion } from "@/features/viewer/hooks/usePrefersReducedMotion";
import { useStageStore } from "@/features/viewer/stageStore";
import { capture } from "@/lib/analytics";
import { ApiError, api } from "@/lib/api";
import { i18n } from "@/lib/i18n";
import { appShellTitle } from "@/lib/seo/head";

// The front door (DESIGN.md §5 "show before you ask"). The warm studio sweep is
// already alive behind this chrome (the persistent StageHost), lit and waiting —
// the empty Stage the pet's cushion will appear on. This route is DOM-only: the
// drop / tap / camera surface over that Stage. On a successful upload it navigates
// to /preview/$jobId; because the canvas lives in __root it never unmounts, so the
// lit sweep holds through the worker's run and the reveal plays in the SAME scene
// — one continuous moment.

// Client guard before we bother the API. The API does the authoritative checks
// (ADR-0030); this is just fast, friendly feedback for the obvious misses.
// Kept in step with the server's MAX_FILE_SIZE_BYTES so the "too large" copy
// names the same limit the upload endpoint actually enforces.
const MAX_BYTES = 15 * 1024 * 1024;
type ErrorKey = "wrongType" | "tooLarge" | "tooSmall" | "animated" | "failed";

// The server runs the authoritative Stage-1 validation (ADR-0030) and returns a
// typed error code on a 400 (e.g. `unsupported_format`). Map that code to the
// SAME warm, specific copy the client-side guard uses, so a rejected upload
// tells the user WHY — pick a different file — instead of a generic "try again"
// they'd hit forever with the same unsupported photo (#327). Anything unmapped
// (network blip, `internal_error`) falls back to the retry-friendly "failed".
function errorKeyFor(err: unknown): ErrorKey {
  if (!(err instanceof ApiError)) return "failed";
  switch (err.code) {
    case "unsupported_format":
      return "wrongType";
    case "animation_not_supported":
      return "animated";
    case "file_too_large":
    case "image_too_large":
    case "decode_bomb":
      return "tooLarge";
    case "file_too_small":
    case "image_too_small":
      return "tooSmall";
    default:
      return "failed";
  }
}

// How long the bottom chrome takes to fade out (must match the Tailwind
// `duration-700` below). We hold the route mounted at least this long before
// navigating to /preview, so the form visibly DISSOLVES instead of being torn
// off on the route change when the upload returns near-instantly.
const CHROME_FADE_MS = 700;

function validate(file: File): ErrorKey | null {
  if (!file.type.startsWith("image/")) return "wrongType";
  if (file.size > MAX_BYTES) return "tooLarge";
  return null;
}

function Studio() {
  const { t } = useTranslation("uploads");
  const router = useRouter();
  const reducedMotion = usePrefersReducedMotion();
  const inputRef = useRef<HTMLInputElement>(null);

  const [status, setStatus] = useState<"idle" | "uploading">("idle");
  const [error, setError] = useState<ErrorKey | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // The Stage behind us holds the empty lit warm sweep while we wait for a photo
  // (no cushion yet — the lit field is the waiting state, ADR-0041 §2).
  useEffect(() => {
    useStageStore.getState().showGhost();
  }, []);

  const openPicker = () => {
    if (status !== "uploading") inputRef.current?.click();
  };

  async function onFile(file: File | undefined) {
    if (!file || status === "uploading") return;
    const bad = validate(file);
    if (bad) {
      setError(bad);
      return;
    }
    setError(null);
    setStatus("uploading"); // starts the chrome + scrim fade-out

    // Funnel: the buyer committed a photo (ADR-0050). The shape is no longer pre-picked
    // here (the controls moved entirely to the post-reveal preview), so we report the
    // store's current/default shape straight off the store — no PII.
    capture("upload_started", { shape: useStageStore.getState().shape });

    try {
      // Run the fade clock ALONGSIDE the upload and await whichever is slower, so
      // the chrome always finishes dissolving before we swap to /preview — without
      // adding latency when the upload itself already took longer than the fade. A
      // reduced-motion visitor skips the wait (no animation to see anyway).
      const faded = reducedMotion
        ? Promise.resolve()
        : new Promise((resolve) => setTimeout(resolve, CHROME_FADE_MS));
      const { jobId } = await api.uploads.create(file);
      await faded;
      await router.navigate({ to: "/preview/$jobId", params: { jobId } });
    } catch (err) {
      // Keep the user here with a warm message; the wireframe Stage stays behind.
      // Surface the server's specific reason (bad format, too large, …) so the
      // user knows to pick a different file rather than retry the same one (#327).
      setError(errorKeyFor(err));
      setStatus("idle");
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    onFile(e.dataTransfer.files?.[0]);
  }

  const ctaLabel = dragActive
    ? t("dragActive")
    : status === "uploading"
      ? t("uploading")
      : t("cta");

  return (
    // Full-area surface: a drag-and-drop target (desktop); the bottom CTA is the
    // tap / click / keyboard control (mobile + a11y). pointer-events re-enabled
    // here (Layout makes the chrome layer pass-through to the canvas behind), so
    // the ghost Stage shows through.
    // biome-ignore lint/a11y/noStaticElementInteractions: the only INTERACTIONS here are pointer-only drag-and-drop (a desktop convenience); the keyboard/SR control is the <button> below, so the surface needs no role.
    <section
      className="pointer-events-auto absolute inset-0 flex flex-col"
      data-testid="studio-surface"
      onDragEnter={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={(e) => {
        // Only clear when leaving the surface, not when moving over a child.
        if (e.currentTarget === e.target) setDragActive(false);
      }}
      onDrop={onDrop}
    >
      {/* Drag-over wash: a soft warm veil + ring, NOT a dashed dropzone box
          (which would read as a form widget inside the cinematic). */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-3 rounded-3xl ring-2 ring-primary/60 transition-opacity duration-200 ${
          dragActive ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background: "radial-gradient(80% 60% at 50% 45%, rgba(240,117,86,0.12), transparent 70%)",
        }}
      />

      {/* Legibility scrim: a soft rise of the Stage's own surround tone behind the
          bottom copy, so the warm field doesn't wash out the text. Calms the lower
          third while the lit sweep stays crisp up top. Kept light (a gentle wash,
          not a heavy band — Leon's feedback). It FADES OUT with the
          chrome once uploading starts, so the form dissolves into the reveal
          rather than popping off on the route change. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 bottom-0 h-1/2 transition-opacity duration-700 ease-out ${
          status === "uploading" ? "opacity-0" : "opacity-100"
        }`}
        style={{
          background:
            "linear-gradient(to top, rgba(231,221,202,0.66) 0%, rgba(231,221,202,0.32) 36%, transparent 78%)",
        }}
      />

      {/* Bottom-anchored chrome (mobile thumb zone). Headline + one CTA + a quiet
          reassurance — the Stage carries the wow, the copy stays calm. Fades out
          (with the scrim) the moment uploading starts, so the form gracefully
          dissolves into the reveal rather than vanishing on the route change. */}
      <div
        className={`relative mt-auto flex flex-col items-center gap-4 px-6 pb-[max(5.5rem,calc(env(safe-area-inset-bottom)+4.5rem))] text-center transition-opacity duration-700 ease-out ${
          status === "uploading" ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <div className="max-w-md">
          <h1 className="text-pretty text-3xl font-semibold tracking-tight text-foreground drop-shadow-sm sm:text-4xl">
            {t("headline")}
          </h1>
          <p className="mt-2 text-balance text-base text-foreground/80">{t("subhead")}</p>
        </div>

        <Button
          type="button"
          size="lg"
          className="min-h-13 w-full max-w-xs rounded-full text-base font-semibold shadow-lg shadow-primary/30 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-primary/40 active:translate-y-0 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
          disabled={status === "uploading"}
          aria-busy={status === "uploading"}
          data-testid="upload-submit"
          onClick={openPicker}
        >
          {status === "uploading" ? (
            <span className="inline-flex items-center gap-2">
              <span
                aria-hidden="true"
                className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
              />
              {ctaLabel}
            </span>
          ) : (
            ctaLabel
          )}
        </Button>

        <p className="text-sm text-foreground/70">{t("reassure")}</p>

        {/* Errors: warm + inline, announced. The ghost keeps morphing behind. */}
        <p
          role="status"
          aria-live="polite"
          className={`min-h-5 text-sm text-alert transition-opacity ${error ? "opacity-100" : "opacity-0"}`}
          data-testid="upload-error"
        >
          {error ? t(`errors.${error}`) : ""}
        </p>
      </div>

      {/* The real, keyboard-accessible control. accept=image/* lets mobile offer
          camera + library; no `capture` so the user keeps that choice. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        data-testid="upload-file-input"
        onChange={(e) => onFile(e.target.files?.[0] ?? undefined)}
      />

      {/* The Stage controls bar is intentionally ABSENT here (Leon, #ui-polish): the
          upload Stage is an empty lit sweep with nothing to shape yet, so the bar only
          appears on /preview once a cushion has been generated. The shape/size choice
          moves entirely into the preview's post-reveal controls. */}
    </section>
  );
}

export const Route = createFileRoute("/upload")({
  head: () => ({ meta: [{ title: appShellTitle(i18n.t("shells.upload")) }] }),
  component: Studio,
});
